import office from '@/taskpane/officejs/office-wrapper'
import { IObservable, IObserver } from '@/taskpane/common/observer-pattern'

export interface IOfficeObservable {
  check(): void
  detachAll(): Promise<void>
  interval: number
}

export default abstract class OfficeObservable<T> implements IObservable<T>, IOfficeObservable {
  abstract get(): Promise<T>
  abstract eventHandlerType(): Office.EventType | null
  public interval: number = 2500

  public state: T
  private usingHandler: boolean = false
  private observers: IObserver<T>[] = []

  async attach(observer: IObserver<T>, defaultState?: T): Promise<void> {
    const observerExists = this.observers.includes(observer)
    if (!observerExists) {
      this.state = defaultState
      this.tryAddHandler()
      this.observers.push(observer)
    }
  }

  async detach(observer: IObserver<T>): Promise<void> {
    const observerIndex = this.observers.indexOf(observer)
    if (observerIndex > 0) {
      this.observers.splice(observerIndex, 1)
    }

    if (this.observers.length == 0) {
      await this.tryRemoveHandler()
    }
  }

  async detachAll(): Promise<void> {
    this.observers.forEach(async (observer) => {
      await this.detach(observer)
    })
  }

  async check() {
    if (!this.isHandlerAvailable() && this.observers.length > 0) {
      try {
        const newState = await this.get()
        if (this.isStateChanged(newState)) {
          this.state = newState
          this.notify()
        }
      } catch {
        // ignore error
      }
    }
  }

  notify(): void {
    for (const observer of this.observers) {
      observer.update(this.state)
    }
  }

  private isStateChanged(newState: T): boolean {
    if (!this.state && !newState) {
      return false
    } else if (typeof this.state === 'string') {
      return this.state !== newState
    } else if (this.state instanceof Array && newState instanceof Array) {
      if ((this.state && !newState) || (!this.state && newState)) {
        return true
      } else if (this.state.length != newState.length) {
        return true
      } else if (JSON.stringify(this.state) !== JSON.stringify(newState)) {
        return true
      } else {
        return false
      }
    } else {
      return JSON.stringify(this.state) !== JSON.stringify(newState)
    }
  }

  private isHandlerAvailable(): boolean {
    return this.eventHandlerType() !== null
  }

  private async tryAddHandler() {
    if (this.isHandlerAvailable() && !this.usingHandler) {
      this.usingHandler = await office.handlers.add(this.eventHandlerType(), async () => {
        this.state = await this.get()
        this.notify()
      })

      if (!this.usingHandler) {
        // don't try to add this handler again
        this.eventHandlerType = () => null
      }
    }
  }

  private async tryRemoveHandler() {
    if (this.usingHandler) {
      this.usingHandler = !(await office.handlers.remove(this.eventHandlerType()))
    }
  }
}
